import { ProjectDate } from './ProjectDate'
import { Contact } from './Contact'
import { ProjectsService } from '@/api/client'
import type { Project as TProjectData } from '@/api/client'
import { DateTime } from 'luxon'
import { Entity, PrimaryColumn, Column, OneToMany, BaseEntity } from 'typeorm'
import handleRequest from '@/helpers/handleRequest'
import DatabaseManager from '@/classes/manager/DatabaseManager'
import { report } from '@/classes/manager/ExceptionReportingManager'
import { chunk } from 'lodash'

@Entity('projects')
export class Project extends BaseEntity {
  @PrimaryColumn('int')
  id!: number

  @Column('varchar')
  name!: string

  @Column('varchar')
  number!: string

  @Column({ type: 'varchar', nullable: true })
  start_date!: string | null

  @Column({ type: 'varchar', nullable: true })
  end_date!: string | null

  @Column({ type: 'varchar', nullable: true })
  rrule!: string | null

  @Column('varchar')
  destination_name!: string

  @Column('varchar')
  destination_street!: string

  @Column('varchar')
  destination_zip!: string

  @Column('varchar')
  destination_city!: string

  @Column('varchar')
  contact_name!: string

  @Column('varchar')
  contact_phone!: string

  @Column('varchar')
  customer!: string

  @Column('int')
  customer_id!: number

  @Column('varchar')
  description!: string

  @Column('boolean')
  show_description!: boolean

  @Column('boolean')
  completable!: boolean

  @Column({ type: 'varchar', nullable: true })
  completed_date!: string | null

  @Column({ type: 'varchar', nullable: true })
  deleted_at!: string | null

  @Column('varchar')
  qr_token!: string

  @Column('varchar')
  icon_class!: string

  @Column({ type: 'int', nullable: true })
  parent_id!: number | null

  @OneToMany(() => ProjectDate, (projectDate) => projectDate.project, {
    eager: true,
  })
  project_dates!: ProjectDate[]

  @OneToMany(() => Contact, (contact) => contact.project, {
    eager: true,
  })
  contacts!: Contact[]

  children!: Project[]

  parent!: Project | null

  async complete() {
    if (this.rrule) {
      // TODO: flag completed in projectStore
      return
    }
    if (this.completable) {
      this.completed_date = DateTime.now().toISO()
      return await this.save()
    }
    throw new Error('This project is not completable')
  }

  async uncomplete() {
    if (this.completable) {
      this.completed_date = null
      return await this.save()
    }
    throw new Error('This project is not uncompletable')
  }

  static async sync() {
    const data = (await handleRequest(ProjectsService.getProjects()))?.data
    if (!data) return
    try {
      const newProjects = []
      const newProjectDates = []
      const newContacts = []
      for (const projectData of data) {
        const project = Project.createFromData(projectData)
        if (projectData.project_dates) {
          for (const projectDateData of projectData.project_dates) {
            // existing projectDates are deleted when the related project is deleted
            const projectDate = ProjectDate.createFromData(
              projectDateData,
              project,
            )
            newProjectDates.push(projectDate)
          }
        }
        if (projectData.contacts) {
          for (const contactData of projectData.contacts) {
            // existing projectDates are deleted when the related project is deleted
            const contact = Contact.createFromData(contactData, project)
            newContacts.push(contact)
          }
        }
        newProjects.push(project)
      }

      const queryRunner = DatabaseManager.appDataSource?.createQueryRunner()
      await queryRunner?.startTransaction()

      try {
        await Project.clear()
        const projectChunks = chunk(newProjects, 999) // sqlite cant handle more than 999 variables in a statement
        for (const chunk of projectChunks) {
          await Project.upsert(chunk, ['id'])
        }
        await ProjectDate.clear()
        const projectDateChunks = chunk(newProjectDates, 999) // sqlite cant handle more than 999 variables in a statement
        for (const chunk of projectDateChunks) {
          await ProjectDate.upsert(chunk, ['id'])
        }
        await Contact.clear()
        const contactChunks = chunk(newContacts, 999) // sqlite cant handle more than 999 variables in a statement
        for (const chunk of contactChunks) {
          await Contact.upsert(chunk, ['id'])
        }
        await queryRunner?.commitTransaction()
      } catch (e) {
        report(e as Error)
        await queryRunner?.rollbackTransaction()
      }
    } catch (e) {
      console.error('Error while syncing projects', e)
    }
  }

  static async syncOne(projectId: number) {
    try {
      const { data: projectData } = await ProjectsService.getProject(projectId)
      if (!projectData) return
      const project = await Project.findOne({ where: { id: projectId } })
      if (project) {
        await Project.createQueryBuilder()
          .where('id = :id', {
            id: project.id,
          })
          .delete()
          .execute()
      }
      const newProject = Project.createFromData(projectData)
      const newProjectDates = []
      if (projectData.project_dates) {
        for (const projectDateData of projectData.project_dates) {
          const projectDate = ProjectDate.createFromData(
            projectDateData,
            newProject,
          )

          newProjectDates.push(projectDate)
        }
      }
      await Project.upsert(newProject, ['id'])
      await ProjectDate.upsert(newProjectDates, ['id'])
    } catch (e) {
      console.error('Error while syncing project with id: ' + projectId, e)
    }
  }

  public static createFromData(projectData: TProjectData): Project {
    const project = new Project()

    project.name = projectData.name
    project.id = projectData.id
    project.parent_id = projectData.parent_id
    project.number = projectData.number
    project.start_date = projectData.start_date
    project.end_date = projectData.end_date
    project.rrule = projectData.rrule
    project.description = projectData.description
    project.show_description = Boolean(projectData.show_description)
    project.completable = projectData.completable
    project.completed_date = projectData.completed_date
    project.deleted_at = projectData.deleted_at
    project.customer = projectData.customer
    project.customer_id = projectData.customer_id
    project.destination_name = projectData.destination_name
    project.destination_street = projectData.destination_street
    project.destination_city = projectData.destination_city
    project.destination_zip = projectData.destination_zip
    project.contact_name = projectData.contact_name
    project.contact_phone = projectData.contact_phone
    project.qr_token = projectData.qr_token
    project.icon_class = projectData.icon_class ?? 'fas fa-house'
    project.project_dates = []
    project.contacts = []
    project.children = []

    return project
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static isProject(obj: any): obj is Project {
    return (
      obj &&
      typeof obj.id === 'number' &&
      typeof obj.name === 'string' &&
      typeof obj.number === 'string' &&
      (obj.start_date === null || typeof obj.start_date === 'string') &&
      (obj.end_date === null || typeof obj.end_date === 'string') &&
      (obj.rrule === null || typeof obj.rrule === 'string') &&
      typeof obj.destination_name === 'string' &&
      typeof obj.destination_street === 'string' &&
      typeof obj.destination_zip === 'string' &&
      typeof obj.destination_city === 'string' &&
      typeof obj.contact_name === 'string' &&
      typeof obj.contact_phone === 'string' &&
      typeof obj.customer === 'string' &&
      typeof obj.customer_id === 'number' &&
      typeof obj.description === 'string' &&
      typeof obj.show_description === 'boolean' &&
      typeof obj.completable === 'boolean' &&
      (obj.completed_date === null || typeof obj.completed_date === 'string') &&
      (obj.deleted_at === null || typeof obj.deleted_at === 'string') &&
      typeof obj.qr_token === 'string' &&
      typeof obj.icon_class === 'string' &&
      (obj.parent_id === null || typeof obj.parent_id === 'number') &&
      Array.isArray(obj.project_dates) &&
      Array.isArray(obj.project_dates) &&
      Array.isArray(obj.children) &&
      obj.children.every(Project.isProject) &&
      (obj.parent === null || Project.isProject(obj.parent))
    )
  }
}
